import {
  FluidSkeletonConfig,
  FluidSkeletonType,
  FluidTheme,
  FluidThemeInterface,
} from '@lmig/fluid-core';
import { Component, Element, h, Prop } from '@stencil/core';

import { Utils } from '../../utils/utils';
import {
  SkeletonButtonGroup,
  SkeletonCard,
  SkeletonCircle,
  SkeletonForm,
  SkeletonImage,
  SkeletonTable,
  SkeletonText,
} from './fluid-loading-skeleton-bones';

/**
 * @displayName Loading Skeleton
 *
 */
@Component({
  tag: 'fluid-loading-skeleton',
  styleUrl: 'fluid-loading-skeleton.scss',
  shadow: true,
})
export class FluidLoadingSkeleton implements FluidThemeInterface {
  // ================================================================ //
  // -- Host Element

  /**
   * Get a reference to the host element
   */
  @Element() host: HTMLElement;

  // ================================================================ //
  // -- Props

  /**
   * What theme settings should the component use?
   * @type {string}
   */
  @Prop({ mutable: true }) overrideGlobalTheme: boolean;

  /**
   * If FluidThemeProvider is used, should this component override the
   * global theme with its local theme?
   */
  @Prop({ mutable: true }) theme: FluidTheme = FluidTheme.CORP;

  /**
   * Single configuration object for the skeleton
   */
  @Prop({ mutable: true }) config: FluidSkeletonConfig;

  /**
   * Called every time the component is connected to the DOM.
   * @action: setGlobalTheme - updates theme to match global theme & subscribes to changes
   */
  connectedCallback(): void {
    Utils.setGlobalTheme(this);
  }

  renderSkeleton() {
    const defaultContent = (
      <div style={{ minHeight: `${this.config?.minHeight || 200}px` }}></div>
    );
    switch (this.config?.skeletonType) {
      case FluidSkeletonType.TABLE:
        return <SkeletonTable {...this.config} />;
      case FluidSkeletonType.BUTTON_GROUP:
        return <SkeletonButtonGroup {...this.config} />;
      case FluidSkeletonType.CARD:
        return (
          <SkeletonCard {...this.config}>
            <slot>{defaultContent}</slot>
          </SkeletonCard>
        );
      case FluidSkeletonType.TEXT:
        return <SkeletonText {...this.config} />;
      case FluidSkeletonType.IMAGE:
        return <SkeletonImage {...this.config} />;
      case FluidSkeletonType.CIRCLE:
        return <SkeletonCircle {...this.config} />;
      case FluidSkeletonType.FORM:
        return <SkeletonForm {...this.config} />;
      default:
        return <SkeletonButtonGroup columns={4} />;
    }
  }

  /**
   * Main Render function
   */
  render() {
    const componentWrapper = `fluid-loading-skeleton theme-${this.theme}`;
    return <div class={componentWrapper}>{this.renderSkeleton()}</div>;
  }
}
